'use client'

import Messages from '@/constants/Messages'
import { useEffect, useState } from 'react'
// import ReactFlagsSelect from 'react-flags-select'
import { Tooltip } from 'react-tooltip'
import {
  Common,
  dynamic,
  HttpService,
  Modal,
  RingBuilder,
  ServiceEndpoints,
  Skeleton
} from '@/config/NextImport'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, AppState } from '@/lib/store'
import { addToCart, getCartProducts } from '@/lib/features/cartSlice'
import Select from 'react-dropdown-select'
import KlarnaMessaging from '../KlarnaMessaging'
import { getPriceColumnFromServer } from '../../lib/priceColumn'
import GoogleReviewBanner from '../home/GoogleReviewBanner'

const ProductPrice = dynamic(() => import('../common/ProductPrice'))
const ReactFlagsSelect = dynamic(() => import('react-flags-select'), {
  ssr: false, // Prevents server-side errors since this uses browser APIs
  loading: () => <div className="h-10 w-40 animate-pulse bg-gray-200" /> // Optional: a placeholder
})
const WishlistIcon = dynamic(() => import('@/components/common/WishlistIcon'), {
  ssr: false,
  loading: () => {
    return (
      <img
        src={Common.setImagePath('assets/images/icon/wishlist.svg')}
        alt="wishlist-icon"
      />
    )
  }
})

// Function to update JSON-LD schema client-side
const updateJsonLdSchema = (productData: any) => {
  if (typeof window === 'undefined') return

  const baseUrl = process.env.NEXT_PUBLIC_WEB_URL
  const productUrl = `${baseUrl}${window.location.pathname}`
  const images = [
    productData.base_image,
    ...(productData.other_image || []).filter((img: any) => !!img)
  ].filter(Boolean)

  const schemaData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: productData.name,
    image: images,
    description: productData.description,
    sku: productData.sku,
    brand: {
      '@type': 'Brand',
      name: "Harmony Jewels"
    },
    offers: [
      {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'USD',
        price: productData.US_price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: ['US', 'VI']
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 1,
              unitCode: 'd'
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 5,
              maxValue: 9,
              unitCode: 'd'
            }
          }
        }
      },
      {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'GBP',
        price: productData.GB_price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'GB'
          },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: {
              '@type': 'QuantitativeValue',
              minValue: 0,
              maxValue: 1,
              unitCode: 'd'
            },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: 3,
              maxValue: 7,
              unitCode: 'd'
            }
          }
        }
      },
      {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'AUD',
        price: productData.AU_price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'AU'
          }
        }
      },
      {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'CAD',
        price: productData.CA_price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'CA'
          }
        }
      },
      {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'NZD',
        price: productData.NZ_price,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'NZ'
          }
        }
      }
    ],
    hasMerchantReturnPolicy: {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: 'GB',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 30,
      returnFees: 'https://schema.org/FreeReturn',
      refundType: 'https://schema.org/FullRefund'
    }
  }

  // Find existing JSON-LD script tag with Product type
  const existingScript = document.querySelector('script[type="application/ld+json"]')
  if (existingScript) {
    const content = JSON.parse(existingScript.textContent || '{}')
    if (content['@type'] === 'Product') {
      existingScript.textContent = JSON.stringify(schemaData)
    }
  }
}

const Options = (props: any) => {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const priceColumn = getPriceColumnFromServer()
  const { settingOptions } = useSelector((state: AppState) => state.settings)
  const [currencyData, setCurrencyData] = useState<any>([])
  const [selectedSku, setSelectedSku] = useState<any>(props.sku)
  const dispatch = useDispatch<AppDispatch>()
  const { cartListProducts } = useSelector((state: AppState) => state.cart)
  const [optionValues, setOptionValues] = useState<any>(
    props.variations.filter(
      (obj1: any, i: any, arr: any) =>
        arr.findIndex((obj2: any) => obj2.option_value === obj1.option_value) === i
    ) || []
  )
  const [selectedProductOptions, setSelectedProductOptions] = useState<any>(
    props.selectedOptions || []
  )
  const [selectedstandAloneOptions, setselectedstandAloneOptions] = useState<any>(
    props.initialSelectedStandalone
  )
  const [productSizes, setProductSizes] = useState<any>(props.productSizes)
  const [selectedCountry, setselectedCountry] = useState<any>(props.defalutSelectedCountry)
  const [selectedRingSizeId, SetselectedRingSizeId] = useState<any>([
    { label: props.defaultSelectedRingSizeValue, value: props.defaultSelectedRingSizeValue }
  ])
  const [productDetail, setProductDetail] = useState<any>(
    props.products.filter((x: any) => x.sku === props.sku)[0]
  )
  const [IsAskDiamondSelection, setIsAskDiamondSelection] = useState<boolean>(true)
  const [IsGemstoneSelected, setIsGemstoneSelected] = useState<boolean>(false)
  const [IsShowDiamondSelectionPopup, setIsShowDiamondSelectionPopup] = useState<boolean>(false)
  const { userData } = useSelector((state: AppState) => state.user)
  const [loading, setLoading] = useState<boolean>(true)
  const [isExistInCart, setIsExistInCart] = useState<boolean>(false)
  const [contactUs, setContactUs] = useState<any>({
    phone: '',
    email: ''
  })

  useEffect(() => {
    if (settingOptions?.length > 0) {
      setContactUs({
        phone: settingOptions.filter((item: any) => item.hj_key === Messages.CustomerCareNumber)[0]
          .hj_value,
        email:
          settingOptions.filter((item: any) => item.hj_key === Messages.CustomerSupportEmail)[0]
            ?.hj_value ?? ''
      })
    }
  }, [settingOptions])

  useEffect(() => {
    if (selectedProductOptions?.length === 0) return

    const sku = getSKUFromSelectedOptions()
    if (Common.isNullorEmpty(sku) || sku === '') return
    setSelectedSku(sku)
    const productData = props.products.filter((x: any) => x.sku === sku)[0]
    let qty = 1
    if (cartListProducts?.products?.length > 0 && productData !== undefined) {
      const product = cartListProducts?.products.filter(
        (x: any) => x.setting_sku === productData.sku && x.diamond_sku === ''
      )

      qty = product.length > 0 ? product[0].qty : 1
    }
    productData.shank_metal = selectedProductOptions[0]?.selected_value
    // productData.setting_url = window.location.href
    productData.qty = qty
    setProductDetail(productData)

    const url = `${pathname.split('-sku-')[0]}-sku-${sku}`
    window.history.replaceState(null, '', url)
    
    // Update JSON-LD schema client-side
    updateJsonLdSchema(productData)
  }, [selectedProductOptions])

  useEffect(() => {
    const storageSteps: any = RingBuilder.getRingBuilder()
    const isGemstoneStepCompleted = storageSteps.filter(
      (item: any) => item.step === 'diamond' && item.IsGemstone && item.IsCompleted
    )
    setIsGemstoneSelected(isGemstoneStepCompleted.length > 0)
    const isDiamondStepCompleted = storageSteps.filter(
      (item: any) => item.step === 'diamond' && item.IsCompleted
    )
    if (isDiamondStepCompleted.length > 0) {
      setIsAskDiamondSelection(false)
    }
    const currency = JSON.parse(Common.decryptData(localStorage.getItem(Messages.Currency)))
    if (currency) {
      setCurrencyData(
        currency.filter((item: any) => item.Code === priceColumn.replace('_price', ''))
      )
    }
  }, [])
  useEffect(() => {
    // const cartId = localStorage.getItem('last_customized_product_id')
    if (cartListProducts?.products?.length !== 0 && productDetail !== undefined) {
      const CartData =
        cartListProducts?.products &&
        cartListProducts.products.find(
          (x: any) => x.setting_sku === props.sku && x.diamond_sku === ''
        )
      if (CartData !== undefined && CartData !== null) {
        setselectedstandAloneOptions(CartData.standalone_options)
        setselectedCountry(CartData.size_country)
        SetselectedRingSizeId([{ label: CartData.size_id, value: CartData.size_id }])
        setProductSizes(
          props.productSizesArray.filter((x: any) => x.country_code === CartData.size_country)[0]
            ?.sizes
        )
      }
    }
    const productExistsInCart = cartListProducts?.products?.some(
      (item: any) => item.setting_sku === productDetail?.sku && item.diamond_sku === ''
    )

    setIsExistInCart(!!productExistsInCart)
    setLoading(false)
  }, [cartListProducts?.products])

  const getSKUFromSelectedOptions = () => {
    let res = ''
    for (let index = 0; index < props.products.length; index++) {
      if (res === '') {
        let cnt = 0
        props.variations
          .filter(
            (obj1: any, i: any, arr: any) =>
              arr.findIndex((obj2: any) => obj2.option_value === obj1.option_value) === i
          )
          .filter((x: any) => x.sku === props.products[index].sku)
          .map((pa: any) => {
            const val = selectedProductOptions.filter(
              (x: any) => x.attribute_code === pa.attribute_code
            )[0]?.selected_value
            if (pa.option_value === val) {
              ++cnt
            }
            if (cnt === selectedProductOptions.length) {
              res = props.products[index].sku
            }
            return true
          })
      }
    }

    return res
  }

  const onOptionClick = (opt: any) => {
    const obj = selectedProductOptions.map((item: any) => {
      if (item.id === opt.attribute_id) {
        return {
          ...item,
          selected_value: opt.option_value,
          selected_value_label: opt.value,
          attribute_id: opt.attribute_id
        }
      }
      return item
    })
    updateOptions(opt)
    setSelectedProductOptions(obj)
  }

  const updateOptions = (opt: any) => {
    const qualifiedSkus = props.variations
      .filter((x: any) => x.option_value === opt.option_value)
      .map((item: any) => item.sku)
    const filteredData = props.variations.filter(
      (item: any) => qualifiedSkus?.includes(item.sku) || item.attribute_id === opt.attribute_id
    )
    const uniqueData = filteredData.filter(
      (item: any, index: any, self: any) =>
        index === self.findIndex((t: any) => t.value === item.value)
    )
    setOptionValues(uniqueData)
  }

  const onStandAloneoOptionClick = (opt: any) => {
    setselectedstandAloneOptions((prev: any[]) => {
      const index = prev.findIndex((item) => item.attribute_id === opt.attribute_id)
      if (index !== -1) {
        const updated = [...prev]
        updated[index] = {
          ...opt,
          selected_value: opt.option_value,
          selected_value_label: opt.value,
          attribute_id: opt.attribute_id
        }
        return updated
      } else {
        return [
          ...prev,
          {
            ...opt,
            selected_value: opt.option_value,
            selected_value_label: opt.value,
            attribute_id: opt.attribute_id
          }
        ]
      }
    })
  }

  const handleCountrydropdown = (e: any) => {
    setselectedCountry(e)
    if (e !== undefined && e !== null) {
      const Sizes = props.productSizesArray.filter((x: any) => x.country_code === e)[0].sizes
      setProductSizes(Sizes)
      SetselectedRingSizeId({ label: Sizes[0], value: Sizes[0] })
    }
    const products = Common.GetJSON(Common.decryptData(localStorage.getItem(Messages.CartProducts)))
    for (const key in products) {
      if (products[key].setting_sku === productDetail.sku && products[key].diamond_sku === '') {
        products[key].size_country = e
      }
    }
    localStorage[Messages.CartProducts] = Common.encryptData(JSON.stringify(products))
  }

  const handleRingSize = async (value: any) => {
    SetselectedRingSizeId(value)
    const products = Common.GetJSON(Common.decryptData(localStorage.getItem(Messages.CartProducts)))
    for (const key in products) {
      if (products[key].setting_sku === productDetail.sku && products[key].diamond_sku === '') {
        products[key].size_id = value[0].value
      }
    }
    localStorage[Messages.CartProducts] = Common.encryptData(JSON.stringify(products))
  }
  const addProductToCart = async () => {
    let ringSize
    if (selectedRingSizeId[0].value !== undefined) {
      ringSize = selectedRingSizeId[0].value
    } else {
      ringSize = productSizes !== undefined ? productSizes[0] : 0
    }
    await dispatch(
      addToCart({
        item: productDetail,
        ringSizeId: ringSize,
        user: userData.user,
        country: selectedCountry,
        standaloneOptions: JSON.stringify(selectedstandAloneOptions)
      })
    )
    // setCartListProducts({
    //   shipping_charge: null,
    //   shipping_country: '',
    //   shipping_method: '',
    //   shipping_postal_code: '',
    //   shipping_methods: []
    // })
    await dispatch(getCartProducts())
    router.push('/cart')
  }

  const nextStepDetails = async (isFirst: boolean = true) => {
    let categories = productDetail.category_ids.split(',')
    let isDiamond = 0
    if (!isFirst) {
      const storageSteps: any = RingBuilder.getRingBuilder()
      categories = storageSteps[0].product.category_ids.split(',')
      isDiamond = storageSteps[0].step === 'diamond' ? 1 : 0
    }
    const param = {
      category_ids: categories.join(',').replace(/^,|,$/g, ''),
      is_diamond: isDiamond,
      setting_default_shape: productDetail.defaultShape,
      carat_from: productDetail.can_be_set_with[0].carat_from,
      carat_to: productDetail.can_be_set_with[0].carat_to
    }
    return new Promise((resolve) => {
      HttpService.postApi(ServiceEndpoints.getSecondStepUrl, param).then((response) => {
        try {
          resolve(response.result[0])
        } catch (error) {
          console.log(error)
        }
      })
    })
  }

  const proceedToNextStep = async (e: any, type: string = 'diamond') => {
    e.preventDefault()

    const cartId = localStorage.getItem('last_customized_product_id')
    if (cartId !== null) {
      const products = Common.GetJSON(
        Common.decryptData(localStorage.getItem(Messages.CartProducts))
      )
      for (const key in products) {
        if (products[key].id === cartId) {
          products[key].standalone_options = selectedstandAloneOptions
        }
      }
      localStorage[Messages.CartProducts] = Common.encryptData(JSON.stringify(products))
    }

    let storageSteps: any = RingBuilder.getRingBuilder()
    const productData = {
      category_ids: productDetail.category_ids,
      name: productDetail.name,
      price: productDetail.price,
      special_price: productDetail.special_price,
      formated_special_price: productDetail.formated_special_price,
      formated_price: productDetail.formated_price,
      image: productDetail.base_image,
      can_be_set_with: productDetail.can_be_set_with,
      attribute_options: productDetail.attribute_options,
      setting_default_shape: productDetail.defaultShape,
      model_number: productDetail.model_number,
      selected_standalone_options: JSON.stringify(selectedstandAloneOptions)
    }
    if (storageSteps === undefined || storageSteps[0].IsCompleted === false) {
      const slug = pathname.split('/')
      const finalUrl = pathname?.includes('-sku-')
        ? `${slug.slice(0, slug.length - 1).join('/')}`
        : pathname
      // 1st Step is Setting
      const responseData: any = await nextStepDetails()
      storageSteps[0].selected_productid = productDetail.sku
      storageSteps[0].product = productData
      storageSteps[0].IsCompleted = true
      const search = searchParams.toString() ? `?${searchParams.toString()}` : ''
      storageSteps[0].detail_url =
        responseData.setting_url !== undefined
          ? responseData.setting_url.split('?')[0] + search
          : pathname + search
      storageSteps[0].url =
        responseData.setting_url !== undefined ? responseData.setting_url.split('?')[0] : finalUrl
      storageSteps[0].IsActive = false
      storageSteps[1].IsActive = true
      RingBuilder.setRingBuilder(storageSteps)
      let diamondUrl = responseData.diamond_url
      if (type === 'gemstone') diamondUrl = responseData.gemstone_url
      router.push(diamondUrl)
    } else if (storageSteps[0].IsCompleted === true && storageSteps[1].IsCompleted === true) {
      // when both steps get completed and user change it from review step
      // const sSku = storageSteps.filter((x) => x.step === 'setting')[0].selected_productid
      const sSku = productDetail.sku
      const dSku = storageSteps.filter((x: any) => x.step === 'diamond')[0].selected_productid
      const stepIndex = storageSteps.findIndex((item: any) => item.step === 'setting')
      storageSteps[stepIndex].selected_productid = productDetail.sku
      storageSteps[stepIndex].product = productData
      storageSteps[0].IsActive = false
      storageSteps[1].IsActive = false
      storageSteps[2].IsActive = true
      storageSteps[2].url = '/complete-jewel?j=' + dSku + '&s=' + sSku
      RingBuilder.setRingBuilder(storageSteps)
      router.replace(`/complete-jewel?j=${dSku}&s=${sSku}`)
    } else if (storageSteps[0].IsCompleted === true) {
      if (storageSteps[0].step === 'setting') {
        // when user comes from the 2nd step without completing the ring builder
        const responseData: any = await nextStepDetails()
        const productData = {
          category_ids: productDetail.category_ids,
          name: productDetail.name,
          price: productDetail.price,
          special_price: productDetail.special_price,
          formated_special_price: productDetail.formated_special_price,
          formated_price: productDetail.formated_price,
          image: productDetail.base_image,
          can_be_set_with: productDetail.can_be_set_with,
          attribute_options: productDetail.attribute_options,
          setting_default_shape: productDetail.defaultShape,
          model_number: productDetail.model_number,
          selected_standalone_options: JSON.stringify(selectedstandAloneOptions)
        }
        storageSteps[0].selected_productid = productDetail.sku
        storageSteps[0].product = productData
        storageSteps[0].IsCompleted = true
        storageSteps[0].detail_url = pathname
        RingBuilder.setRingBuilder(storageSteps)
        let diamondUrl = responseData.diamond_url
        if (type === 'gemstone') diamondUrl = responseData.gemstone_url
        router.push(diamondUrl)
      } else {
        // when 1st completed (diamond) and 2nd step is setting in ring builder
        // const responseData = await nextStepDetails(false)
        const productData = {
          category_ids: productDetail.category_ids,
          name: productDetail.name,
          price: productDetail.price,
          special_price: productDetail.special_price,
          formated_special_price: productDetail.formated_special_price,
          formated_price: productDetail.formated_price,
          image: productDetail.base_image,
          can_be_set_with: productDetail.can_be_set_with,
          attribute_options: productDetail.attribute_options,
          setting_default_shape: productDetail.defaultShape,
          model_number: productDetail.model_number,
          selected_standalone_options: JSON.stringify(selectedstandAloneOptions)
        }
        storageSteps[1].selected_productid = productDetail.sku
        storageSteps[1].product = productData
        storageSteps[1].IsCompleted = true
        storageSteps[0].IsActive = false
        storageSteps[1].IsActive = false
        storageSteps[2].IsActive = true
        // storageSteps[1].detail_url = responseData.setting_url.split('?')[0] + path.search
        const diamondStep = storageSteps.filter((x: any) => x.step === 'diamond')
        const diamondSku = diamondStep[0].selected_productid
        const settingStep = storageSteps.filter((x: any) => x.step === 'setting')
        const settingSku = settingStep[0].selected_productid
        storageSteps[2].url = '/complete-jewel?j=' + diamondSku + '&s=' + settingSku
        RingBuilder.setRingBuilder(storageSteps)
        storageSteps = RingBuilder.getRingBuilder()
        router.replace(`/complete-jewel?j=${diamondSku}&s=${settingSku}`)
      }
    }
  }

  return (
    <>
      <div className="product-summary-top">
        <div className="product-summary-share-title">
          <h1 className="product-summary-title">
            {productDetail.name !== undefined &&
            selectedProductOptions?.map((x: any) => x.attribute_name)?.includes('Metal')
              ? Common.toTitleCase(
                  productDetail.name +
                    ' in ' +
                    selectedProductOptions
                      ?.filter(
                        (x: any) =>
                          // x.attribute_code === 'bridalshape' ||
                          x.attribute_name === 'Metal'
                      )
                      .map((x: any) => x.selected_value_label)
                      .toString()
                      .replaceAll(',', ' & ')
                )
              : Common.toTitleCase(productDetail.name)}
          </h1>
        </div>
        <p className={'product-summary-sku'}>SKU:{selectedSku}</p>
        <GoogleReviewBanner
          PositionCenter={false}
          ShowKeyword={true}
        />
      </div>
      <div className="product-summary-middle">
        {props.options.length > 0 &&
          props.options?.map((item: any, itemIndex: number) =>
            item.show_in_detail_page === 1 &&
            optionValues.filter((option: any) => option.attribute_code === item.attribute_code)
              .length > 0 ? (
              <div
                className="product-summary-filter"
                key={itemIndex}
              >
                <div className="filter-type">
                  {item.input_type !== 'dropdown' ? (
                    <div className="filter-title">
                      {item.attribute_name}:
                      <span>
                        {selectedProductOptions.filter((x: any) => x.attribute_id === item.id)
                          .length > 0
                          ? selectedProductOptions.filter((x: any) => x.attribute_id === item.id)[0]
                              ?.selected_value_label
                          : ''}
                        <a
                          className="tooltip-info cursor ms-2"
                          data-tooltip-id="two-tone-band"
                          data-tooltip-content="If you’d like a two tone band please select your metal preferences and we’ll make them accordingly."
                          data-tooltip-place="top"
                        >
                          <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="fas"
                            data-icon="circle-info"
                            className="svg-inline--fa fa-circle-info text-custom"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <path
                              fill="currentColor"
                              d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                            ></path>
                          </svg>
                        </a>
                        <Tooltip id="two-tone-band" />
                      </span>
                    </div>
                  ) : (
                    <div className="filter-title">{item.attribute_name}</div>
                  )}
                </div>
                {item.input_type === 'dropdown' ? (
                  <div
                    className="select-size-box"
                    style={{ maxWidth: '100px' }}
                  >
                    <select
                      className="filter-dropdown"
                      onChange={(e) =>
                        onOptionClick(
                          optionValues.find(
                            (option: any) =>
                              option.option_value === e.target.value &&
                              option.attribute_name === item.attribute_name
                          )
                        )
                      }
                      value={
                        selectedProductOptions.find((x: any) => x.attribute_id === item.id)
                          ?.selected_value || ''
                      }
                    >
                      {optionValues
                        .filter((option: any) => option.attribute_code === item.attribute_code)
                        .map((option: any) => (
                          <option
                            key={option.option_value}
                            value={option.option_value}
                          >
                            {option.value}
                          </option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <ul className="filter-type-list">
                    {optionValues
                      .filter((option: any) => option.attribute_code === item.attribute_code)
                      .map((option: any) => (
                        <li
                          className={
                            selectedProductOptions.filter(
                              (x: any) =>
                                x.selected_value === option.option_value &&
                                x.attribute_id === option.attribute_id
                            ).length > 0
                              ? 'filter-type-list-item current-item'
                              : 'filter-type-list-item'
                          }
                          key={option.option_value}
                        >
                          <span
                            onClick={() => onOptionClick(option)}
                            className={
                              'product-listing-img-link' +
                              (option.value_image === '' ? ' btn-type-list-item' : '')
                            }
                          >
                            {option.value_image !== '' &&
                            item.input_type === Messages.FilterInputTypes.Radio ? (
                              <img
                                src={option.value_image}
                                alt={option.value}
                              />
                            ) : (
                              <>{option.value}</>
                            )}
                          </span>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            ) : (
              ''
            )
          )}

        <div className={props.category?.is_gift_card ? 'd-none' : 'product-summary-info'}></div>
      </div>

      <div className="product-summary-middle mt-0">
        {props.options?.map((item: any, itemIndex: number) =>
          item.Is_standalone === 1 &&
          props.standAloneOptions?.filter(
            (option: any) => option.attribute_code === item.attribute_code
          ).length > 0 ? (
            <div
              className="product-summary-filter"
              key={itemIndex}
            >
              <div className="filter-type">
                {item.input_type !== 'dropdown' ? (
                  <div className="filter-title">
                    {item.attribute_name}:
                    <span>
                      {selectedstandAloneOptions?.filter((x: any) => x.attribute_id === item.id)
                        .length > 0
                        ? selectedstandAloneOptions?.filter(
                            (x: any) => x.attribute_id === item.id
                          )[0]?.selected_value_label
                        : ''}
                      <span>
                        <a
                          className="tooltip-info cursor ms-2"
                          data-tooltip-id="two-tone-band"
                          data-tooltip-content="If you’d like a two tone band please select your metal preferences and we’ll make them accordingly."
                          data-tooltip-place="top"
                        >
                          <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="fas"
                            data-icon="circle-info"
                            className="svg-inline--fa fa-circle-info text-custom"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                          >
                            <path
                              fill="currentColor"
                              d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                            ></path>
                          </svg>
                        </a>
                        <Tooltip place="top" />
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="filter-title">{item.attribute_name}</div>
                )}
              </div>
              {item.input_type === 'dropdown' ? (
                <div
                  className="select-size-box"
                  style={{ maxWidth: '100px' }}
                >
                  <select
                    className="filter-dropdown"
                    onChange={(e) =>
                      onStandAloneoOptionClick(
                        props.standAloneOptions.find(
                          (option: any) =>
                            option.option_value === e.target.value &&
                            option.attribute_name === item.attribute_name
                        )
                      )
                    }
                    value={
                      selectedstandAloneOptions.find((x: any) => x.attribute_id === item.id)
                        ?.selected_value || ''
                    }
                  >
                    {props.standAloneOptions
                      .filter((option: any) => option.attribute_code === item.attribute_code)
                      .map((option: any) => (
                        <option
                          key={option.option_value}
                          value={option.option_value}
                        >
                          {option.value}
                        </option>
                      ))}
                  </select>
                </div>
              ) : (
                <ul className="filter-type-list">
                  {props.standAloneOptions
                    .filter((option: any) => option.attribute_code === item.attribute_code)
                    .map((option: any) => (
                      <li
                        className={
                          selectedstandAloneOptions?.filter(
                            (x: any) =>
                              x.selected_value === option.option_value &&
                              x.attribute_id === option.attribute_id
                          ).length > 0
                            ? 'filter-type-list-item current-item'
                            : 'filter-type-list-item'
                        }
                        key={option.option_value}
                      >
                        <span
                          onClick={() => onStandAloneoOptionClick(option)}
                          className={
                            'product-listing-img-link' +
                            (option.value_image === '' ? ' btn-type-list-item' : '')
                          }
                        >
                          {option.value_image !== '' &&
                          item.input_type === Messages.FilterInputTypes.Radio ? (
                            <img
                              src={option.value_image}
                              alt={option.value}
                            />
                          ) : (
                            <>{option.value}</>
                          )}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          ) : null
        )}
      </div>

      {productSizes !== undefined && productSizes.length !== 0 && (
        <div className="row select-size-row">
          <div className="ring-size-title">Size</div>
          {loading ? (
            <div className="select-size-box d-flex gap-3">
              <Skeleton
                baseColor={'#f1f1f1'}
                highlightColor={'#e7dde1'}
                height={38}
                width={212}
              />
              <Skeleton
                baseColor={'#f1f1f1'}
                highlightColor={'#e7dde1'}
                height={38}
                width={135}
              />
            </div>
          ) : (
            <>
              <div
                className="select-size-box pe-2"
                style={{ maxWidth: '240px' }}
              >
                <ReactFlagsSelect
                  countries={props.countries}
                  selected={selectedCountry}
                  fullWidth={true}
                  onSelect={(e) => handleCountrydropdown(e)}
                />
              </div>
              <div
                className="select-size-box ps-0"
                style={{ maxWidth: '150px' }}
              >
                <Select
                  options={productSizes.map((value: any) => ({
                    label: value,
                    value
                  }))}
                  values={
                    selectedRingSizeId.length > 0
                      ? selectedRingSizeId
                      : [{ label: productSizes[0], value: productSizes[0] }]
                  }
                  onChange={(value) => handleRingSize(value)}
                  backspaceDelete={false}
                  dropdownHeight="200px"
                  placeholder="select size"
                  color="#767676"
                  dropdownPosition="auto"
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className="product-summary-bottom">
        <div className="product-summary-price">
          <span>
            <ProductPrice data={productDetail} />
            {!props.category?.is_mto_category && <small>(Setting Only)</small>}
          </span>
        </div>
        {/* <p className="price-vat-text">Price is for the setting only.</p> */}
        <div className="product-summary-btn">
          {loading ? (
            <div className="d-flex gap-2 mx-2">
              <Skeleton
                baseColor={'#f1f1f1'}
                highlightColor={'#e7dde1'}
                height={40}
                width={210}
              />
              <Skeleton
                baseColor={'#f1f1f1'}
                highlightColor={'#e7dde1'}
                height={40}
                width={225}
              />
            </div>
          ) : (
            <>
              <button
                className="btn btn-custom"
                onClick={() => addProductToCart()}
              >
                {isExistInCart ? <>Update Your Cart</> : <>Buy Setting Only</>}
              </button>
              {!props.category?.is_mto_category && IsAskDiamondSelection && (
                <button
                  className="btn add-to-cart-btn btn-custom"
                  onClick={() => setIsShowDiamondSelectionPopup(true)}
                >
                  Choose This Setting
                </button>
              )}
              {(props.category?.is_mto_category === 0 ||
                props.category?.is_mto_category === null) &&
                !IsAskDiamondSelection && (
                  <button
                    className="btn btn-custom"
                    onClick={(e) => proceedToNextStep(e)}
                  >
                    <>Select This Setting</>
                  </button>
                )}
            </>
          )}

          <div className="btn wishlist-btn btn-custom-light">
            <WishlistIcon item={productDetail} />
          </div>
          <span
            className="btn message-btn btn-custom-light"
            data-bs-toggle="modal"
            data-bs-target="#DropHintModal"
            data-backdrop="static"
            data-keyboard="false"
          >
            <img
              src={Common.setImagePath('assets/images/icon/drop-hint-icon.svg')}
              alt="Drop A Hint"
            />
          </span>
        </div>
        {currencyData.length > 0 && (
          <KlarnaMessaging
            amount={parseFloat(productDetail[priceColumn]) * 100}
            currency={currencyData.length > 0 ? currencyData[0].currency_name : 'GBP'}
            countryCode={currencyData.length > 0 ? currencyData[0].Code : 'GB'}
          />
        )}
        <div className="summary-info">
          <span className="delivery-icon-anim me-2">
            <img
              src={Common.setImagePath('assets/images/icon/delivery-icon.svg')}
              className="delivery-icon me-2"
              width="22px"
              alt="Delivery"
            />
          </span>
          <div className="summary-para">
            <span>
              {Messages.DeliveryNotes}
              <span className="tooltip-info cursor">
                <b style={{ cursor: 'text' }}>Need it sooner? </b>
                <b>
                  <a
                    href={`tel:${contactUs?.phone || '#'}`}
                    className="underline "
                  >
                    Call us now.
                  </a>
                </b>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="circle-info"
                  className="svg-inline--fa fa-circle-info text-custom"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  data-tooltip-id="delivery-date"
                  data-tooltip-content={`Delivery date varies according to the selected ring setting and ${IsGemstoneSelected ? 'gemstone' : 'diamond'}. Accurate delivery date is available on the complete ring page.`}
                  data-tooltip-place="top"
                  data-tooltip-variant="light"
                >
                  <path
                    fill="currentColor"
                    d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                  ></path>
                </svg>
                {/* <FontAwesomeIcon
                  className="text-custom mx-1"
                  icon={faInfoCircle}
                  data-tooltip-id="delivery-date"
                  data-tooltip-content="Delivery date varies according to the selected ring setting and diamond. Accurate delivery date is available on the complete ring page."
                  data-tooltip-place="top"
                  data-tooltip-variant="light"
                /> */}
                <Tooltip id="delivery-date" />
              </span>
            </span>
          </div>
        </div>
      </div>

      <Modal
        centered
        show={IsShowDiamondSelectionPopup}
        onHide={() => setIsShowDiamondSelectionPopup(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <h4 className="w-100 text-center"> </h4>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setIsShowDiamondSelectionPopup(false)}
          ></button>
        </Modal.Header>
        <Modal.Body className="pt-0 choose-modal-body">
          <div className="row text-center">
            <h4 className="pb-3">Choose any one option to continue</h4>
            <div className="col-md-12 mb-3">
              <button
                className="btn add-to-cart-btn btn-custom"
                onClick={(e) => proceedToNextStep(e, 'diamond')}
              >
                Add A Diamond
              </button>
            </div>
            <div className="col-md-12">
              <button
                className="btn btn-black black-custom-hover"
                onClick={(e) => proceedToNextStep(e, 'gemstone')}
              >
                Add A Gemstone
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default Options
